import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState, useCallback, useRef, useEffect } from "react";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import TournamentCard from "@/components/TournamentCard";
import { useRushHistoryGlobal } from "@/lib/hooks/useApi";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { t } from "@/lib/i18n";

function GrandRushHistory() {
  const router = useRouter();
  const [page, setPage] = useState(1);
  const [allTournaments, setAllTournaments] = useState<
    NonNullable<ReturnType<typeof useRushHistoryGlobal>["data"]>
  >([]);
  const [hasMore, setHasMore] = useState(true);

  const { data, isLoading, isFetching } = useRushHistoryGlobal('grand_rush', page);

  // Accumulate tournament data as pages load
  useEffect(() => {
    if (data && data.length > 0) {
      setAllTournaments((prev) => {
        const existingIds = new Set(prev.map((t) => t.tournamentId));
        const newTournaments = data.filter(
          (t) => !existingIds.has(t.tournamentId),
        );
        return [...prev, ...newTournaments];
      });

      if (data.length < 30) {
        setHasMore(false);
      }
    } else if (data && data.length === 0 && page > 1) {
      setHasMore(false);
    }
  }, [data, page]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetching, hasMore],
  );

  if (isLoading && page === 1) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="bg-[#1D2A52] flex flex-1 flex-col h-full overflow-hidden p-1 gap-4">
      <FloatingLeftButton
        onClick={() => router.navigate({ to: "/grand-rush" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      <div className="flex-1 flex flex-col p-1 gap-4 w-full min-h-0 mt-10">
        <div className="text-center flex flex-col gap-2">
          <p className="text-white text-3xl font-extrabold text-shadow-black">
            {t("rushHistory.grandRushHistory")}
          </p>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <ScrollArea className="flex-1 min-h-0 mt-3" type="scroll">
            <div className="space-y-3">
              {allTournaments.length ? (
                <>
                  {allTournaments.map((tournament, index) => (
                    <div
                      key={`${tournament.tournamentId}-${index}`}
                      className="w-full"
                      ref={
                        index === allTournaments.length - 1
                          ? lastElementRef
                          : undefined
                      }
                    >
                      <TournamentCard
                        tournament={tournament}
                        asLink
                        hideUserWinnings
                        isHistoryGlobal
                        hideMultiplier
                      />
                    </div>
                  ))}
                  {isFetching && hasMore && page > 1 && (
                    <div className="flex items-center justify-center py-4">
                      <Spinner />
                    </div>
                  )}
                  {!hasMore && allTournaments.length > 0 && (
                    <div className="flex items-center justify-center py-4 text-white/50 text-sm">
                      {t("rushHistory.youveReachedTheEnd")}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-white/70">
                  <p className="text-lg font-semibold">
                    {t("rushHistory.noTournamentsFound")}
                  </p>
                  <p className="text-sm">
                    {t("rushHistory.checkBackLater")}
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/(game)/grand-rush/history")({
  component: GrandRushHistory,
});
